import { Container, Rectangle, Sprite, Text, Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import { lerpColor } from '../game/views/utils/lerpColor';
import { PageType } from './types';
import { soundManager } from '../app/SoundManager';
import { animateBrightnessFlash } from '../utils/pixi/animations/filter-animation-utils';
import { EventScheduler } from '../utils/EventScheduler';

type LayoutState = 'vertical' | 'horizontal';

type MainMenuPageButtonConfig = {
  activeIcon: string;
  inactiveIcon: string;
  lockedIcon: string;
  label: string;
  layoutAvailability: LayoutState[];
  iconScale?: number;
};

const configs: Record<PageType, MainMenuPageButtonConfig> = {
  [PageType.Home]: {
    activeIcon: 'Active_Home_Tab',
    inactiveIcon: 'Inactive_Home_Tab',
    lockedIcon: '',
    label: 'Home',
    layoutAvailability: ['vertical', 'horizontal'],
  },
  [PageType.Store]: {
    activeIcon: 'Active_Store_Tab',
    inactiveIcon: 'Inactive_Store_Tab',
    lockedIcon: 'Locked_Store_Tab',
    label: 'Store',
    layoutAvailability: ['vertical', 'horizontal'],
  },
  [PageType.Tournament]: {
    activeIcon: 'Active_Tournament_Tab',
    inactiveIcon: 'Inactive_Tournament_Tab',
    lockedIcon: 'Locked_Tournament_Tab',
    label: 'Tournament',
    layoutAvailability: ['vertical', 'horizontal'],
  },
  [PageType.Settings]: {
    activeIcon: 'settings',
    inactiveIcon: 'settings',
    lockedIcon: 'settings',
    label: 'Settings',
    iconScale: 1.35,
    layoutAvailability: ['horizontal'],
  },
};

export class MainMenuPageButtonView extends Container {
  public readonly onClicked = new Signal();

  private readonly eventScheduler = new EventScheduler();

  private readonly wrapper = new Container().props({
    parent: this,
  });

  private readonly inactiveIcon = new Sprite().props({
    anchor: { x: 0.5, y: 1 },
    y: -18,
    parent: this.wrapper,
  });

  private readonly activeIcon = new Sprite().props({
    anchor: { x: 0.5, y: 1 },
    y: -18,
    parent: this.wrapper,
  });

  private readonly notificationDot = new Sprite().props({
    anchor: { x: 0.5, y: 0.5 },
    x: 40,
    y: -110,
    parent: this.wrapper,
    texture: 'Notification_Dot'
  });

  private readonly labelText = new Text({
    text: 'label',
    style: {
      fontFamily: 'Kanit',
      fontSize: 30,
      stroke: { width: 6, color: '#2e0200' },
      fill: 'white',
    },
  }).props({
    parent: this.wrapper,
    anchor: 0.5,
  });

  private readonly inputSprite = new Sprite().props({
    parent: this,
  });

  private layoutAvailability: LayoutState[] = [];

  private horizontalLayout = false;

  private active = false;

  constructor() {
    super();
    this.initTicker();
  }

  private initTicker(): void {
    const ticker = new Ticker();
    const callback = (): void => {
      this.update(ticker.deltaTime);
    };
    ticker.add(callback);
    ticker.start();

    this.on('destroyed', () => {
      ticker.destroy();
    });
  }

  public init(type: PageType, locked = false, hasNotification = false): this {
    const { activeIcon, inactiveIcon, label, lockedIcon, iconScale, layoutAvailability } = configs[type];

    this.layoutAvailability = layoutAvailability;

    this.labelText.text = getLocalizedText(label);

    if (locked) return this.initLocked(type);

    this.toggleNotification(hasNotification);

    this.activeIcon.props({ texture: locked ? lockedIcon : activeIcon, scale: iconScale ?? 1 });
    this.inactiveIcon.props({
      texture: locked ? lockedIcon : inactiveIcon,
      width: this.activeIcon.width,
      height: this.activeIcon.height,
      scale: iconScale ?? 1,
    });

    this.inputSprite.eventMode = 'static';
    this.inputSprite.cursor = 'pointer';
    this.inputSprite.hitArea = new Rectangle(-50, -100, 100, 120);
    this.inputSprite.on('pointerdown', () => {
      soundManager.play('UI_Button_Click');
      this.onClicked.emit();
    });

    return this;
  }

  private initLocked(type: PageType): this {
    // FIXME Hugo needs to fix those assets

    [this.activeIcon, this.inactiveIcon].forEach((icon) => {
      icon.props({
        scale: 1.44,
        texture: configs[type].lockedIcon,
      });
    });

    return this;
  }

  public setActive(value: boolean, skipAnimation = false): this {
    this.active = value;

    const wrapperTarget = { scaleX: value ? 1 : 0.6, scaleY: value ? 1 : 0.6 };
    const textTarget = { tint: value ? 0xf0ffed : 0xffd19f };
    const rootTarget = this.horizontalLayout && value ? { pivotY: -30 } : { pivotY: 0 };
    const activeIconTarget = { alpha: value ? 1 : 0 };
    const inactiveIconTarget = { alpha: value ? 0 : 1 };

    this.visible = this.layoutAvailability.includes(this.horizontalLayout ? 'horizontal' : 'vertical');

    if (skipAnimation || !this.visible) {
      this.props(rootTarget);
      this.labelText.props(textTarget);
      this.wrapper.props(wrapperTarget);
      this.activeIcon.props(activeIconTarget);
      this.inactiveIcon.props(inactiveIconTarget);
    } else {
      const duration = 300;

      const fromTint = this.labelText.tint;

      const colorLerpTarget = { v: 0 };
      this.labelText
        .tweenCreate(colorLerpTarget)
        .to({ v: 1 }, duration, 'quadOut', true)
        .onUpdate.add(() => {
          if (this.labelText.destroyed) return;
          this.labelText.tint = lerpColor(fromTint, textTarget.tint, colorLerpTarget.v);
        });

      this.tweenTo(rootTarget, duration, 'quadOut', true);
      this.wrapper.tweenTo(wrapperTarget, duration, 'quadOut', true);
      this.activeIcon.tweenTo(activeIconTarget, duration, 'quadOut', true);
      this.inactiveIcon.tweenTo(inactiveIconTarget, duration, 'quadOut', true);
    }

    return this;
  }

  public toggleNotification(value: boolean): void {
    this.notificationDot.visible = value;

    this.eventScheduler.reset();
    if (value) {
      this.eventScheduler.addEvent(
        () => {
          this.notificationDot.tweenTo({ scaleXY: 1.4 }, 400, 'quadOut', true, 0, 0, true);
          animateBrightnessFlash(this.notificationDot, 2, 300, 'quadOut');
        },
        2000,
        true,
      );
    }
  }

  public setHorizontalLayout(value: boolean): void {
    if (this.horizontalLayout === value) return;
    this.horizontalLayout = value;
    this.setActive(this.active, true);
  }

  private update(deltaTime: number): void {
    this.eventScheduler.update(deltaTime);
  }
}
